/**
 * Fuente única de verdad para los valores enumerados del dominio.
 *
 * En el esquema Prisma estas columnas son `String`, no enums nativos, para que
 * el mismo esquema funcione en SQLite (desarrollo) y PostgreSQL (producción)
 * sin mantener dos archivos que se desincronizan. La validación real ocurre
 * aquí y en los esquemas Zod que derivan de estos arrays.
 */
// ─────────────────────────────────────────────────────────────
// Identidad y control de acceso
// ─────────────────────────────────────────────────────────────
/**
 * Roles ordenados de mayor a menor privilegio. El orden importa: `tieneAlMenosRol`
 * compara por posición en este array.
 */
export const ROLES = ['OWNER', 'ADMIN', 'GESTOR', 'FINANZAS', 'LECTOR'];
export const ETIQUETAS_ROL = {
    OWNER: 'Dueño',
    ADMIN: 'Administrador',
    GESTOR: 'Gestor de Licitaciones',
    FINANZAS: 'Finanzas',
    LECTOR: 'Solo Lectura',
};
/** `true` si `rol` tiene al menos tantos privilegios como `minimo`. */
export function tieneAlMenosRol(rol, minimo) {
    return ROLES.indexOf(rol) <= ROLES.indexOf(minimo);
}
// ─────────────────────────────────────────────────────────────
// Comercial
// ─────────────────────────────────────────────────────────────
export const TIPOS_ORGANISMO = [
    'MUNICIPALIDAD',
    'SLEP',
    'SERVICIO_PUBLICO',
    'UNIVERSIDAD',
    'FUERZAS_ARMADAS',
    'SALUD',
    'JUDICIAL',
    'OTRO',
];
export const ETIQUETAS_TIPO_ORGANISMO = {
    MUNICIPALIDAD: 'Municipalidad',
    SLEP: 'Servicio Local de Educación Pública',
    SERVICIO_PUBLICO: 'Servicio Público',
    UNIVERSIDAD: 'Universidad',
    FUERZAS_ARMADAS: 'Fuerzas Armadas y de Orden',
    SALUD: 'Salud',
    JUDICIAL: 'Poder Judicial',
    OTRO: 'Otro',
};
export const TIPOS_PROCESO = [
    'LICITACION',
    'COMPRA_AGIL',
    'TRATO_DIRECTO',
    'CONVENIO_MARCO',
];
export const ETIQUETAS_TIPO_PROCESO = {
    LICITACION: 'Licitación Pública',
    COMPRA_AGIL: 'Compra Ágil',
    TRATO_DIRECTO: 'Trato Directo',
    CONVENIO_MARCO: 'Convenio Marco',
};
/**
 * Estados del embudo comercial. `OPORTUNIDAD` → `EN_PREPARACION` →
 * `OFERTA_ENVIADA` → (`ADJUDICADA` | `PERDIDA` | `DESIERTA`).
 *
 * El CRM original tenía cuatro estados; se agregan `EN_PREPARACION` (se decidió
 * postular y se está armando la oferta) y `DESIERTA` (el organismo no adjudicó
 * a nadie, que no es lo mismo que perder).
 */
export const ESTADOS_LICITACION = [
    'OPORTUNIDAD',
    'EN_PREPARACION',
    'OFERTA_ENVIADA',
    'ADJUDICADA',
    'PERDIDA',
    'DESIERTA',
];
export const ETIQUETAS_ESTADO_LICITACION = {
    OPORTUNIDAD: 'Oportunidad',
    EN_PREPARACION: 'En Preparación',
    OFERTA_ENVIADA: 'Oferta Enviada',
    ADJUDICADA: 'Adjudicada',
    PERDIDA: 'Perdida',
    DESIERTA: 'Desierta',
};
/** Estados que cuentan como resultado cerrado para calcular tasa de éxito. */
export const ESTADOS_RESUELTOS = ['ADJUDICADA', 'PERDIDA'];
/** Estados que representan pipeline abierto (aún puede convertirse en ingreso). */
export const ESTADOS_ABIERTOS = [
    'OPORTUNIDAD',
    'EN_PREPARACION',
    'OFERTA_ENVIADA',
];
export const PRIORIDADES = ['ALTA', 'MEDIA', 'BAJA'];
export const ETIQUETAS_PRIORIDAD = {
    ALTA: 'Alta',
    MEDIA: 'Media',
    BAJA: 'Baja',
};
export const MOTIVOS_PERDIDA = [
    'PRECIO',
    'PLAZO',
    'EXPERIENCIA',
    'REQUISITO_TECNICO',
    'ADMINISTRATIVO',
    'NO_SE_PRESENTO',
    'DESCONOCIDO',
];
export const ETIQUETAS_MOTIVO_PERDIDA = {
    PRECIO: 'Precio más alto que el ganador',
    PLAZO: 'Plazo de entrega menos competitivo',
    EXPERIENCIA: 'Menor experiencia acreditada',
    REQUISITO_TECNICO: 'Incumplimiento técnico',
    ADMINISTRATIVO: 'Error u omisión administrativa',
    NO_SE_PRESENTO: 'No alcanzamos a presentar la oferta',
    DESCONOCIDO: 'Desconocido',
};
/**
 * Modalidad de entrada o visualización de montos económicos.
 * `BRUTO`: Valores con IVA incluido (común en presupuestos públicos y retail).
 * `NETO`: Valores antes de impuestos (común en contratos corporativos y contabilidad).
 */
export const MODALIDADES_MONTO = ['BRUTO', 'NETO'];
export const ETIQUETAS_MODALIDAD_MONTO = {
    BRUTO: 'Bruto (con IVA incluido)',
    NETO: 'Neto (sin IVA)',
};
export const PERFILES_COMPETIDOR = [
    'DEPREDADOR_PRECIO',
    'AGRESIVO_PLAZO',
    'LIDER_TECNICO',
    'EQUILIBRADO',
];
export const ETIQUETAS_PERFIL_COMPETIDOR = {
    DEPREDADOR_PRECIO: 'Depredador de Precios (Oferta bajo el 85% del Presupuesto)',
    AGRESIVO_PLAZO: 'Agresivo en Plazos (Entregas significativamente más rápidas)',
    LIDER_TECNICO: 'Líder Técnico (Precio alto, gana por experiencia y pauta técnica)',
    EQUILIBRADO: 'Equilibrado / Estándar',
};
// ─────────────────────────────────────────────────────────────
// Ejecución de proyectos
// ─────────────────────────────────────────────────────────────
export const ESTADOS_PROYECTO = [
    'PLANIFICADO',
    'EN_EJECUCION',
    'EN_PAUSA',
    'CERRADO',
    'CANCELADO',
];
export const ETIQUETAS_ESTADO_PROYECTO = {
    PLANIFICADO: 'Planificado',
    EN_EJECUCION: 'En Ejecución',
    EN_PAUSA: 'En Pausa',
    CERRADO: 'Cerrado',
    CANCELADO: 'Cancelado',
};
/** Estados en los que el proyecto todavía consume recursos y genera alertas. */
export const ESTADOS_PROYECTO_ACTIVOS = [
    'PLANIFICADO',
    'EN_EJECUCION',
    'EN_PAUSA',
];
export const SALUD_PROYECTO = ['SANO', 'ALERTA', 'CRITICO'];
export const ETIQUETAS_SALUD_PROYECTO = {
    SANO: 'En regla',
    ALERTA: 'En alerta',
    CRITICO: 'Crítico',
};
// ─────────────────────────────────────────────────────────────
// Costos y gastos
// ─────────────────────────────────────────────────────────────
/**
 * `DIRECTO`: imputable a un proyecto concreto (subcontratos, viáticos de terreno).
 * `INDIRECTO`: gasto general de la empresa (contabilidad, arriendo, licencias base).
 * La distinción permite calcular margen de contribución por proyecto separado
 * del resultado operacional de la empresa.
 */
export const TIPOS_CATEGORIA_GASTO = ['DIRECTO', 'INDIRECTO'];
export const TIPOS_DOCUMENTO_GASTO = [
    'FACTURA',
    'BOLETA',
    'BOLETA_HONORARIOS',
    'RECIBO',
    'SIN_DOCUMENTO',
];
export const ESTADOS_PAGO_GASTO = ['PENDIENTE', 'PAGADO'];
export const PERIODICIDADES = ['MENSUAL', 'BIMESTRAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL'];
/** Cuántos meses avanza cada periodicidad. */
export const MESES_POR_PERIODICIDAD = {
    MENSUAL: 1,
    BIMESTRAL: 2,
    TRIMESTRAL: 3,
    SEMESTRAL: 6,
    ANUAL: 12,
};
// ─────────────────────────────────────────────────────────────
// Facturación y cobranza
// ─────────────────────────────────────────────────────────────
export const TIPOS_DOCUMENTO_VENTA = [
    'FACTURA_ELECTRONICA',
    'FACTURA_EXENTA',
    'BOLETA',
    'NOTA_CREDITO',
    'NOTA_DEBITO',
];
export const ETIQUETAS_TIPO_DOCUMENTO_VENTA = {
    FACTURA_ELECTRONICA: 'Factura Electrónica',
    FACTURA_EXENTA: 'Factura Exenta',
    BOLETA: 'Boleta',
    NOTA_CREDITO: 'Nota de Crédito',
    NOTA_DEBITO: 'Nota de Débito',
};
/** Documentos que restan del total facturado en vez de sumar. */
export const DOCUMENTOS_VENTA_NEGATIVOS = ['NOTA_CREDITO'];
export const CONDICIONES_PAGO = [
    'CONTADO',
    'DIAS_15',
    'DIAS_30',
    'DIAS_45',
    'DIAS_60',
    'DIAS_90',
];
/** Días corridos que suma cada condición sobre la fecha de emisión. */
export const DIAS_POR_CONDICION_PAGO = {
    CONTADO: 0,
    DIAS_15: 15,
    DIAS_30: 30,
    DIAS_45: 45,
    DIAS_60: 60,
    DIAS_90: 90,
};
export const ETIQUETAS_CONDICION_PAGO = {
    CONTADO: 'Contado',
    DIAS_15: '15 días',
    DIAS_30: '30 días',
    DIAS_45: '45 días',
    DIAS_60: '60 días',
    DIAS_90: '90 días',
};
/**
 * Estados de factura. `PAGADA_PARCIAL`, `PAGADA` y `VENCIDA` NO se escriben a
 * mano: se derivan de los pagos registrados y de la fecha de vencimiento
 * (ver `finanzas/cobranza.ts`). Solo `BORRADOR`, `EMITIDA`, `ENVIADA` y
 * `ANULADA` son estados que fija una persona.
 */
export const ESTADOS_FACTURA = [
    'BORRADOR',
    'EMITIDA',
    'ENVIADA',
    'PAGADA_PARCIAL',
    'PAGADA',
    'VENCIDA',
    'ANULADA',
];
export const ETIQUETAS_ESTADO_FACTURA = {
    BORRADOR: 'Borrador',
    EMITIDA: 'Emitida',
    ENVIADA: 'Enviada al Organismo',
    PAGADA_PARCIAL: 'Pagada Parcialmente',
    PAGADA: 'Pagada',
    VENCIDA: 'Vencida',
    ANULADA: 'Anulada',
};
/**
 * Estados fijados manualmente; el resto los calcula el sistema.
 * Se declara como tupla `as const` para que sirva directamente en `z.enum`.
 */
export const ESTADOS_FACTURA_MANUALES = ['BORRADOR', 'EMITIDA', 'ENVIADA', 'ANULADA'];
/** Estados en los que la factura todavía representa dinero por cobrar. */
export const ESTADOS_FACTURA_COBRABLES = [
    'EMITIDA',
    'ENVIADA',
    'PAGADA_PARCIAL',
    'VENCIDA',
];
export const MEDIOS_PAGO = ['TRANSFERENCIA', 'CHEQUE', 'EFECTIVO', 'COMPENSACION', 'OTRO'];
/**
 * Cómo se genera el cronograma de cobranza de un proyecto. Los cuatro modos que
 * usa Lexicore hoy.
 */
export const MODOS_FACTURACION = ['POR_HITOS', 'AL_CIERRE', 'MENSUAL', 'CONTRA_OC'];
export const ETIQUETAS_MODO_FACTURACION = {
    POR_HITOS: 'Contra hitos / entregables',
    AL_CIERRE: 'Una factura al cierre',
    MENSUAL: 'Mensual (servicio recurrente)',
    CONTRA_OC: 'Contra Orden de Compra',
};
export const ESTADOS_CUOTA = ['PROYECTADA', 'FACTURADA', 'CANCELADA'];
// ─────────────────────────────────────────────────────────────
// Predicción
// ─────────────────────────────────────────────────────────────
export const TIPOS_PREDICCION = ['PRECIO', 'PLAZO'];
export const NIVELES_CONFIANZA = ['ALTA', 'MEDIA', 'BAJA', 'SIN_DATOS'];
export const ETIQUETAS_NIVEL_CONFIANZA = {
    ALTA: 'Alta',
    MEDIA: 'Media',
    BAJA: 'Baja',
    SIN_DATOS: 'Sin datos',
};
// ─────────────────────────────────────────────────────────────
// Auditoría y adjuntos
// ─────────────────────────────────────────────────────────────
export const ACCIONES_AUDITORIA = ['CREAR', 'ACTUALIZAR', 'ELIMINAR', 'LOGIN', 'EXPORTAR', 'IMPORTAR'];
export const ENTIDADES_ADJUNTABLES = [
    'LICITACION',
    'PROYECTO',
    'GASTO',
    'FACTURA',
    'ORGANISMO',
];
// ─────────────────────────────────────────────────────────────
// Calendario
// ─────────────────────────────────────────────────────────────
/**
 * Tipos de evento del calendario unificado. Se conservan los cuatro del CRM
 * original y se agrega `COBRO_PROYECTADO` para las cuotas todavía no facturadas.
 */
export const TIPOS_EVENTO = [
    'PLAZO_POSTULACION',
    'ENTREGA_COMPROMETIDA',
    'FIN_PROYECTO',
    'VENCIMIENTO_FACTURA',
    'COBRO_PROYECTADO',
    'HITO',
    'PAGO_PROVEEDOR',
    'GASTO_RECURRENTE',
];
export const ETIQUETAS_TIPO_EVENTO = {
    PLAZO_POSTULACION: 'Plazo de Postulación / Cierre de Oferta',
    ENTREGA_COMPROMETIDA: 'Entrega Comprometida',
    FIN_PROYECTO: 'Fin de Ejecución de Proyecto',
    VENCIMIENTO_FACTURA: 'Vencimiento de Cobro de Factura',
    COBRO_PROYECTADO: 'Cobro Proyectado (aún no facturado)',
    HITO: 'Hito del Proyecto',
    PAGO_PROVEEDOR: 'Pago Pendiente a Proveedor',
    GASTO_RECURRENTE: 'Gasto Fijo Recurrente',
};
/**
 * Variable CSS de color por tipo de evento. Se mapea a los tokens que ya existen
 * en el diseño original para que el calendario se vea idéntico.
 */
export const COLOR_TIPO_EVENTO = {
    PLAZO_POSTULACION: 'var(--accent-blue)',
    ENTREGA_COMPROMETIDA: 'var(--accent-purple)',
    FIN_PROYECTO: 'var(--accent-green)',
    VENCIMIENTO_FACTURA: 'var(--accent-yellow)',
    COBRO_PROYECTADO: 'var(--accent-gray)',
    HITO: 'var(--accent-blue)',
    PAGO_PROVEEDOR: 'var(--accent-red)',
    GASTO_RECURRENTE: 'var(--accent-red)',
};
//# sourceMappingURL=enums.js.map